import type { OperationVariables, QueryHookOptions } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { getOperationAST } from 'graphql';
import * as _ from 'lodash-es';
import { useLayoutEffect, useRef, useState } from 'react';

const TTL_MS = 10 * 60 * 1000;
const STORAGE_KEY = 'slab-query-ttl-map';

/**
 * A map of query identifiers to the expiration time of the cached data
 */
// eslint-disable-next-line import/no-unused-modules -- expose for testing
export class QueryTTLMap {
  private static rehydrate(): Record<string, number> {
    try {
      const existingDataString = localStorage.getItem(STORAGE_KEY);
      return existingDataString ? JSON.parse(existingDataString) : {};
    } catch {
      return {};
    }
  }

  private static getKey(
    query: Parameters<typeof useQuery>[0],
    variables: OperationVariables | undefined,
  ): string | null {
    const operationName = getOperationAST(query)?.name?.value;
    if (!operationName) return null;

    return _.isEmpty(variables)
      ? operationName
      : `${operationName}/${JSON.stringify(variables)}`;
  }

  private map: Record<string, number> = QueryTTLMap.rehydrate();

  get(
    query: Parameters<typeof useQuery>[0],
    variables: OperationVariables | undefined,
  ) {
    const key = QueryTTLMap.getKey(query, variables);
    return key ? this.map[key] : null;
  }

  /**
   * Extends TTL for the given query key and persists the map without overwriting updates from other tabs.
   */
  touch(
    query: Parameters<typeof useQuery>[0],
    variables: OperationVariables | undefined,
  ) {
    const key = QueryTTLMap.getKey(query, variables);
    if (!key) return;

    this.map[key] = Date.now() + TTL_MS;

    const existingData = QueryTTLMap.rehydrate();

    Object.entries(existingData).forEach(([queryKey, expiredAt]) => {
      if (!this.map[queryKey] || expiredAt > this.map[queryKey]) {
        this.map[queryKey] = expiredAt;
      }
    });

    const now = Date.now();
    Object.entries(this.map).forEach(([queryKey, expiredAt]) => {
      if (expiredAt < now) {
        delete this.map[queryKey];
      }
    });

    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.map));
  }
}

const getOptions = <TData, TVariables extends OperationVariables>(
  queryTTLMap: QueryTTLMap,
  options: Pick<
    QueryHookOptions<TData, TVariables>,
    'skip' | 'variables' | 'errorPolicy'
  >,
  query: UseQueryParameters<TData, TVariables>[0],
) => {
  const expiredAt = queryTTLMap.get(query, options.variables);
  const shouldUseCachedData = expiredAt != null && expiredAt > Date.now();

  return {
    ...options,
    fetchPolicy: shouldUseCachedData ? 'cache-first' : 'cache-and-network',
  } as const;
};

type UseQueryParameters<
  TData,
  TVariables extends OperationVariables = OperationVariables,
> = Parameters<typeof useQuery<TData, TVariables>>;

const useTTLQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: UseQueryParameters<TData, TVariables>[0],
  providedOptions: Pick<
    QueryHookOptions<TData, TVariables>,
    'skip' | 'variables' | 'errorPolicy'
  > = {},
) => {
  const queryTTLMap = useRef<QueryTTLMap | null>(null);
  const getQueryTTLMap = () => {
    if (queryTTLMap.current == null) {
      queryTTLMap.current = new QueryTTLMap();
    }
    return queryTTLMap.current;
  };

  const [options, setOptions] = useState(() =>
    getOptions(getQueryTTLMap(), providedOptions, query),
  );

  const [previousProvidedOptions, setPreviousProvidedOptions] =
    useState(providedOptions);

  if (!_.isEqual(previousProvidedOptions, providedOptions)) {
    setPreviousProvidedOptions(providedOptions);
    setOptions(getOptions(getQueryTTLMap(), providedOptions, query));
  }

  const { data, error, loading } = useQuery(query, options);

  const isDataReady = !loading && data != null;
  useLayoutEffect(() => {
    if (isDataReady) {
      getQueryTTLMap().touch(query, options.variables);
    }
  }, [isDataReady, query, options.variables]);

  return { data, error };
};

export default useTTLQuery;
